import { ActiveLogHelper } from "../Engine/Externals/ActiveLog/ActiveLogHelper";
import { AttachmentsManager } from "../Engine/Externals/Attachments/AttachmentsManager";
import { Form } from "../Engine/Form/Form";
import { IForm } from "../Engine/Form/IForm";
import { noop, stringifyUnknownError } from "../../../Common/TypingUtils";
import { AttachmentState } from "../Engine/Externals/Attachments/Store/AttachmentsReducer";
import { loadInitialAttachments } from "../Engine/Externals/Attachments/Store/AttachmentsActions";
import { FeatureFlagsActionType } from "../Engine/Externals/FeatureFlags/Store/FeatureFlagsActions";
import { MetricsCollector } from "../Controls/Internals/Metrics/MetricsCollector";
import { ModelHunk } from "../Engine/Core/ModelStoring/ModelHunk";
import { updatePortalUserId } from "../Engine/ActionCreators/FormSettingsActionCreators";

import { fetchDataFromServer, fetchInner } from "./FormInitialization/DataFetching";
import { loadRequisitesIntoForm } from "./FormInitialization/LoadingDataIntoForm";
import { loadCalculator2Resources } from "./FormInitialization/LoadCalculator2Resources";
import { setupOnLeavePageHandler } from "./FormInitialization/PageOnLeaveHandler";
import { prepareSettingsAndFeatureFlags } from "./FormInitialization/PrepareSettingsAndFeatureFlags";
import { FormResources } from "./FormResources";
import { autocalcsDisabledInReadOnly, FormSettings } from "./Types/FormSettings";
import { FormTestFakes } from "./Types/FormTestFakes";
import { FeatureFlags } from "./Types/FeatureFlags";

export enum InnerLoadStatus {
    Completed = "Completed",
    NeedNormalization = "NeedNormalization",
}

export async function initialLoadInner(form: IForm): Promise<ModelHunk> {
    const inner = await fetchInner(form, form.settings.model, form.settings.useServerSideFLangNormalization);
    await form.loadDataIntoForm(inner);
    return inner;
}

export async function initForm(resources: FormResources, formTestFakes?: FormTestFakes): Promise<IForm> {
    const { settings, queryFeatureFlags } = prepareSettingsAndFeatureFlags(resources.settings);
    const form = createForm(resources, settings, formTestFakes);
    form.hooks.beforeInit(form.getFormHookCallContext());
    setupOnLeavePageHandler(form.changeSet);

    try {
        const [requisites, attachments, featureFlags] = await fetchDataFromServer(form, resources.requisiteList);
        initializeAttachments(form, attachments);
        loadRequisitesIntoForm(form, requisites);
        initializeFeatureFlags(form, { ...featureFlags, ...queryFeatureFlags });
        MetricsCollector.setMetricsSettings(settings);

        await form.store.dispatch(updatePortalUserId());

        return form;
    } catch (err) {
        if (stringifyUnknownError(err) === "ieSizeError") {
            form.ieSizeError = true;
            return form;
        } else {
            throw err;
        }
    }
}

export async function afterFormInit(form: IForm): Promise<void> {
    await form.hooks.afterInit(form.getFormHookCallContext());
    form.testLogger.log("Start form", { gfv: form.settings.gfv });

    if (process.env.NODE_ENV === "development") {
        window.addEventListener("mousemove", e => {
            form.testLogger.log("Mouse move", { x: e.clientX, y: e.clientY });
        });
    }

    validateAttachments(form);

    const dataVersion = await form.candyApiDraftClient.getDataVersion();
    form.changeSet.setCurrentDataVersion(dataVersion);

    form.changeSet.startAutosend(form.settings.useServerSideCalculations ? 500 : 10000);
}

export function initializeFeatureFlags(form: IForm, featureFlags: FeatureFlags) {
    form.store.dispatch({ type: FeatureFlagsActionType.LoadInitialFeatureFlags, payload: featureFlags });
    form.featureFlags = featureFlags;
}

function initializeAttachments(form: IForm, attachments: AttachmentState): void {
    form.store.dispatch(loadInitialAttachments(attachments));
    form.attachments = new AttachmentsManager(
        form.dataDeclarationHelper,
        form.store,
        form.changeSet,
        form.candyApiDraftClient,
        form
    );
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    form.attachments.initTasks();
}

function validateAttachments(form: IForm): void {
    form.attachments.validateAttachments();
}

export function createForm(resources: FormResources, settings: FormSettings, fakes?: FormTestFakes): IForm {
    const form = new Form(
        {
            dataDeclaration: resources.dataDeclaration,
            lazyLoadDeclaration: resources.lazyLoadDeclaration,
            calculationHooks: resources.calculationHooks,
            calculationFunctions: resources.calculationFunctions || {},
            validationFunctions: resources.validationFunctions || {},
            localizationDictionary: resources.localizationDictionary,
            __onlyForTests__candyApi: fakes?.__onlyForTests__candyApi,
            __onlyForTests__digestApi: fakes?.__onlyForTests__digestApi,
            __onlyForTests__changeSet: fakes?.__onlyForTests__changeSet,
        },
        settings,
        resources.requisiteList,
        resources.resourcesHash
    );
    if (!autocalcsDisabledInReadOnly(settings)) {
        loadCalculator2Resources(form, resources.autocalculationConfigurators);
    }

    if (!form.activeLogHelper) {
        form.activeLogHelper = new ActiveLogHelper(form);
    }

    form.hooks = {
        beforeInit: resources.hooks?.beforeInit ?? noop,
        afterInit: resources.hooks?.afterInit ?? noop,
        beforeUnload: resources.hooks?.beforeUnload ?? noop,
    };
    return form;
}
